import { deferred } from "@client/utils/deferred";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { generateActionId } from "@lib/utils/idGeneration";
import {
  actionIsIdentical,
  ActionMessage,
  roundReducer,
  type Reducer,
  type UnconfirmedActionMessage,
} from "./core";
import type {
  ClientStorageAdapter,
  ConfirmAction,
  MessageToClient,
  MessageToServer,
  PerformActionFailed,
} from "./network";
import type { Action } from "./round/actions";
import type { RoundAggregate } from "./round/aggregate";
import { undoAction } from "./round/undo";

type ActionQueueItem =
  | {
      type: "action";
      action: Action;
      resolve: (result: Error | UnconfirmedActionMessage) => void;
    }
  | {
      type: "undo";
      resolve: (result: Error | UnconfirmedActionMessage | null) => void;
    }
  | {
      type: "redo";
      resolve: (result: Error | UnconfirmedActionMessage | null) => void;
    }
  | {
      type: "handleMessage";
      msg: MessageToClient;
      resolve?: () => void;
    };

export type UndoRedoInfo = {
  message: UnconfirmedActionMessage;
  originalAction: UnconfirmedActionMessage;
  roundBefore: RoundAggregate;
  roundAfter: RoundAggregate;
};

export class Client {
  id: string;
  underlyingUserId: string;
  actingAsUserId: string;
  aggregateId: string;
  reducer: Reducer;
  storage: ClientStorageAdapter;
  onErrorCallback: (errorMsg: string) => void;
  sendMessage?: (msg: MessageToServer) => void;
  updateCallback?: () => void;
  onActionCallback?: (action: UnconfirmedActionMessage) => void;
  offlineMode?: boolean;
  readOnly: boolean;

  // Track the actions taken by this client, since we don't undo actions before that.
  actionsSincePageLoad: Set<string> = new Set();

  isActionPending = false;
  actionQueue: ActionQueueItem[] = [];

  constructor({
    id,
    userId,
    aggregateId,
    onErrorCallback,
    reducer,
    storage,
    offlineMode,
  }: {
    id: string;
    userId: string;
    aggregateId: string;
    onErrorCallback: (errorMsg: string) => void;
    reducer: Reducer;
    storage: ClientStorageAdapter;
    offlineMode?: boolean;
  }) {
    this.id = id;
    this.actingAsUserId = userId;
    this.underlyingUserId = userId;
    this.aggregateId = aggregateId;
    this.onErrorCallback = onErrorCallback;
    this.reducer = reducer;
    this.storage = storage;
    this.offlineMode = offlineMode;
    this.readOnly = false;
  }

  public setReadOnly(readOnly: boolean) {
    this.readOnly = readOnly;
  }

  public async performAction(
    action: Action,
    actionId?: string,
  ): Promise<Error | UnconfirmedActionMessage> {
    const actionMsg: UnconfirmedActionMessage = {
      id: actionId ?? generateActionId(),
      aggregateId: this.aggregateId,
      payload: action,
      actingAsUserId: this.actingAsUserId,
      underlyingUserId: this.underlyingUserId,
      undoes: null,
      redoes: null,
    };

    const result = await this.storage.performUnconfirmedAction(
      this.reducer,
      actionMsg,
    );

    if (result instanceof Error) {
      return result;
    }

    this.actionsSincePageLoad.add(actionMsg.id);

    if (this.offlineMode) {
      return actionMsg;
    }

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    this.onActionCallback?.(actionMsg);
    this.updateCallback?.();

    this.sendMessage({
      type: "PerformAction",
      action: actionMsg,
    });

    return actionMsg;
  }

  public async retryUnconfirmedActions(): Promise<Error | null> {
    if (this.offlineMode) {
      return null;
    }

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    if (this.offlineMode) {
      return new Error("Can't retry actions in offline mode");
    }

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    for (const action of await this.storage.getUnconfirmedActions()) {
      this.sendMessage({
        type: "PerformAction",
        action: action.message,
      });
    }

    return null;
  }

  public async clearUnconfirmedActions() {
    await this.storage.clearUnconfirmedActions();
    this.updateCallback?.();
  }

  public register() {
    const lastConfirmedVersion = this.storage.getLastConfirmedVersion();
    console.log(
      "registering with last confirmed version",
      lastConfirmedVersion,
    );
    if (!this.sendMessage) {
      throw new Error(`Client ${this.id} is not connected`);
    }
    this.sendMessage({ type: "Register", lastConfirmedVersion });
  }

  handleMessage(msg: MessageToClient): Promise<void> {
    console.log(`client ${this.id} received message ${msg.type}`);

    if (msg.type === "PerformActionFailed") {
      console.log("PerformActionFailed", msg);
    } else if (msg.type === "ConfirmActions") {
      console.log(`${msg.actions.length} actions confirmed`);
    }

    return new Promise<void>((resolve) => {
      this.actionQueue.push({
        type: "handleMessage",
        msg,
        resolve,
      });

      void this._startNextPendingAction();
    });
  }

  async _handleMessage(msg: MessageToClient) {
    switch (msg.type) {
      case "PerformActionFailed":
        this._handlePerformActionFailed(msg);
        break;
      case "ConfirmActions":
        await this._handleConfirmAction(msg);
        break;
      default:
        assertUnreachable(msg);
    }
  }

  private _handlePerformActionFailed(msg: PerformActionFailed) {
    this.onErrorCallback(msg.message);
    this.updateCallback?.();
  }

  private async _handleConfirmAction(msg: ConfirmAction) {
    const unconfirmedActions = await this.storage.getUnconfirmedActions();
    const lastConfirmedVersion = this.storage.getLastConfirmedVersion();

    for (const action of msg.actions) {
      if (action.version <= lastConfirmedVersion) {
        const previousAction = await this.storage.getAction(action.id);
        if (
          previousAction &&
          !actionIsIdentical(previousAction.payload, action.payload)
        ) {
          if (
            action.payload.type === "EVALUATION_NOTES_CHANGE" ||
            action.payload.type === "EVALUATION_FEEDBACK_CHANGE"
          ) {
            // These actions might change if the user changes their sharing settings.
            // The could go from redacted to not or vice versa.
            continue;
          }
          console.error(
            `Previous action ${action.id} is not identical to the action from the server`,
          );
          console.error("previous action payload", previousAction.payload);
          console.error("action payload", action.payload);
          throw new Error(
            `Previous action ${action.id} is not identical to the action from the server`,
          );
        }

        continue;
      }

      const error = await this.storage.performConfirmedAction(
        this.reducer,
        action,
      );

      if (!error) {
        continue;
      }

      if (error instanceof Error) {
        this.onErrorCallback(error.message);
      } else {
        for (const e of error) {
          console.error(e);
          this.onErrorCallback(e.error.message);
        }
      }
    }

    for (const action of msg.actions) {
      if (!unconfirmedActions.some(({ message }) => message.id === action.id)) {
        this.onActionCallback?.(action);
      }
    }

    this.updateCallback?.();
  }

  connect(
    sendMessage: (msg: MessageToServer) => void,
    setMessageHandler: (
      handler: (msg: MessageToClient) => Promise<void>,
    ) => void,

    // To be called when the storage adapter has been updated by an action
    updateCallback?: () => void,

    // To be called with confirmed *or* unconfirmed actions, *before* updateCallback.
    // We always call updateCallback after this because in ClientWrapper we want
    // to fill the queue of actions (pendingActions) which is cleared by the render
    // triggered by updateCallback.
    onActionCallback?: (action: UnconfirmedActionMessage) => void,
  ) {
    this.sendMessage = sendMessage;
    setMessageHandler(this.handleMessage.bind(this));
    this.updateCallback = updateCallback;
    this.onActionCallback = onActionCallback;
  }

  // This is intended to be used on startup in case there are any actions that
  // weren't synced before the user closed their tab.
  async sendUnconfirmedActions(): Promise<Error | void> {
    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    const unconfirmedActions = await this.storage.getUnconfirmedActions();
    for (const action of unconfirmedActions) {
      this.sendMessage?.({
        type: "PerformAction",
        action: action.message,
      });
    }
  }

  _undo(): Promise<Error | UnconfirmedActionMessage | null> {
    return this._undoOrRedo("UNDO");
  }

  _redo(): Promise<Error | UnconfirmedActionMessage | null> {
    return this._undoOrRedo("REDO");
  }

  async _undoOrRedo(
    type: "UNDO" | "REDO",
  ): Promise<Error | UnconfirmedActionMessage | null> {
    const actions = [
      ...this.storage.getConfirmedActions(),
      ...(await this.storage.getUnconfirmedActions()).map(
        ({ message }) => message,
      ),
    ];

    let actionMessage: UnconfirmedActionMessage;

    if (type === "UNDO") {
      const undoer = generateUndoAction(actions, this.actingAsUserId);
      if (!undoer) {
        return null;
      }

      if (!this.actionsSincePageLoad.has(undoer.undoneAction.id)) {
        return null;
      }

      let aggregateBefore;
      if ("version" in undoer.undoneAction) {
        // If it's a confirmed action
        aggregateBefore = await this.storage.getAggregateAtVersion(
          this.reducer,
          undoer.undoneAction.version - 1,
        );
        if (aggregateBefore === null) {
          return new Error(
            `Undone action ${undoer.undoneAction.id} not found at version ${undoer.undoneAction.version - 1}`,
          );
        }
      } else {
        // If it's an unconfirmed action
        aggregateBefore =
          await this.storage.getAggregateBeforeUnconfirmedAction(
            this.reducer,
            undoer.undoneAction.id,
          );
        if (aggregateBefore instanceof Error) {
          return aggregateBefore;
        }
      }

      const undoOrRedoAction = undoer.makeAction(aggregateBefore);
      actionMessage = {
        aggregateId: this.aggregateId,
        id: generateActionId(),
        payload: undoOrRedoAction,
        underlyingUserId: this.underlyingUserId,
        actingAsUserId: this.actingAsUserId,
        undoes: undoer.undoneAction.id,
        redoes: null,
      };
    } else {
      const redoer = generateRedoAction(actions, this.actingAsUserId);
      if (!redoer) {
        return null;
      }
      const { undoes, redoes } = redoer;
      if (!this.actionsSincePageLoad.has(redoes)) {
        return null;
      }

      const originalActionMessage = await this.storage.getAction(redoes);
      if (!originalActionMessage) {
        return new Error(`Original action ${redoes} not found`);
      }
      actionMessage = {
        aggregateId: this.aggregateId,
        id: generateActionId(),
        payload: originalActionMessage.payload,
        underlyingUserId: this.underlyingUserId,
        actingAsUserId: this.actingAsUserId,
        undoes,
        redoes,
      };
    }

    const nullOrError = await this.storage.performUnconfirmedAction(
      this.reducer,
      actionMessage,
    );

    if (nullOrError instanceof Error) {
      return nullOrError;
    }

    this.actionsSincePageLoad.add(actionMessage.id);

    if (!this.sendMessage) {
      return new Error(`Client ${this.id} is not connected`);
    }

    this.updateCallback?.();
    this.onActionCallback?.(actionMessage);
    this.sendMessage({
      type: "PerformAction",
      action: actionMessage,
    });

    return actionMessage;
  }

  getAggregate() {
    return this.storage.getUnconfirmedAggregateSync();
  }

  async dispatchUndo(): Promise<Error | UndoRedoInfo | null> {
    return this._dispatchUndoOrRedo("undo");
  }

  async dispatchRedo(): Promise<Error | UndoRedoInfo | null> {
    return this._dispatchUndoOrRedo("redo");
  }

  async _dispatchUndoOrRedo(
    type: "undo" | "redo",
  ): Promise<Error | UndoRedoInfo | null> {
    const { promise, resolve } = deferred<Error | UndoRedoInfo | null>();
    this.actionQueue.push({
      type,
      resolve: async (result: Error | UnconfirmedActionMessage | null) => {
        if (result instanceof Error) {
          resolve(result);
        } else if (result === null) {
          resolve(null);
        } else {
          if (result.undoes === null) {
            throw new Error(`Action ${result.id} is not an undo or redo`);
          }
          let originalAction = await this.storage.getAction(result.undoes);
          if (originalAction === null) {
            throw new Error(`Original action ${result.undoes} not found`);
          }

          let roundBefore: RoundAggregate;
          if (
            "version" in originalAction &&
            typeof originalAction.version === "number"
          ) {
            const agg = await this.storage.getAggregateAtVersion(
              this.reducer,
              originalAction.version - 1,
            );

            if (agg === null) {
              throw new Error(
                `Original action ${result.undoes} not found at version ${originalAction.version - 1}`,
              );
            }

            roundBefore = agg;
          } else {
            const maybeAgg =
              await this.storage.getAggregateBeforeUnconfirmedAction(
                this.reducer,
                result.undoes,
              );
            if (maybeAgg instanceof Error) {
              return maybeAgg;
            }
            if (maybeAgg === null) {
              return new Error(
                `Original action ${result.undoes} not found before unconfirmed action ${result.id}`,
              );
            }
            roundBefore = maybeAgg;
          }

          const reducerResult = await roundReducer.performAction(
            roundBefore,
            result,
          );

          if (reducerResult instanceof Error) {
            resolve(reducerResult);
          } else {
            resolve({
              message: result,
              originalAction,
              roundBefore,
              roundAfter: reducerResult,
            });
          }
        }
      },
    });
    void this._startNextPendingAction();
    return promise;
  }

  async dispatch(action: Action): Promise<Error | UnconfirmedActionMessage> {
    const { promise, resolve } = deferred<Error | UnconfirmedActionMessage>();

    this.actionQueue.push({
      type: "action",
      action,
      resolve,
    });

    void this._startNextPendingAction();

    return promise;
  }

  private async _startNextPendingAction() {
    if (this.isActionPending) {
      return;
    }

    const actionToPerform = this.actionQueue.shift();
    if (!actionToPerform) {
      this.isActionPending = false;
      return;
    }

    this.isActionPending = true;
    switch (actionToPerform.type) {
      case "action": {
        const { action, resolve } = actionToPerform;
        const result = await this.performAction(action);
        resolve(result);
        break;
      }
      case "undo": {
        const { resolve } = actionToPerform;
        const result = await this._undo();
        resolve(result);
        break;
      }
      case "redo": {
        const { resolve } = actionToPerform;
        const result = await this._redo();
        resolve(result);
        break;
      }
      case "handleMessage": {
        const { msg, resolve } = actionToPerform;
        await this._handleMessage(msg);
        if (resolve) resolve();
        break;
      }
      default:
        assertUnreachable(actionToPerform);
    }

    this.isActionPending = false;
    void this._startNextPendingAction();
  }
}

type Undoer = {
  // We need the state of the aggregate before the original action
  // so we know what state to revert back to.
  makeAction: (roundBeforeAction: RoundAggregate) => Action;
  undoneAction: ActionMessage | UnconfirmedActionMessage;
};

function generateUndoAction(
  actions: UnconfirmedActionMessage[],
  userId: string,
): Undoer | null {
  const undoneActionIds: Set<string> = new Set();
  const undosToSkip: Set<string> = new Set();

  for (let i = actions.length - 1; i >= 0; i--) {
    const action = actions[i];

    if (action.actingAsUserId !== userId) {
      continue;
    }

    // Redo
    if (action.redoes !== null && action.undoes !== null) {
      undosToSkip.add(action.undoes);
      continue;
    }

    // Undo
    if (action.redoes === null && action.undoes !== null) {
      if (!undosToSkip.has(action.id)) {
        undoneActionIds.add(action.undoes);
      }
      continue;
    }

    if (!undoneActionIds.has(action.id)) {
      const maybeUndoFn = undoAction(action.payload);
      if (maybeUndoFn) {
        return {
          makeAction: maybeUndoFn,
          undoneAction: action,
        };
      }
    }
  }

  return null;
}

function generateRedoAction(
  actions: UnconfirmedActionMessage[],
  userId: string,
): { undoes: string; redoes: string } | null {
  const redoneActionIds: Set<string> = new Set();

  for (let i = actions.length - 1; i >= 0; i--) {
    const action = actions[i];

    if (action.actingAsUserId !== userId) {
      continue;
    }

    // Redo
    if (action.undoes !== null && action.redoes !== null) {
      // Note that this line is tricky but action.undoes (not action.redoes) is
      // the correct action. We use redoneActionIds to skip over the undo that
      // was canceled out. We will already ignore the original action
      // (action.redoes) because it's not an undo.
      redoneActionIds.add(action.undoes);
      continue;
    }

    // Undo
    if (action.undoes !== null && action.redoes === null) {
      if (!redoneActionIds.has(action.id)) {
        const maybeRedoFn = undoAction(action.payload);
        if (maybeRedoFn) {
          return {
            undoes: action.id,
            redoes: action.undoes,
          };
        }
      }
    }
  }

  return null;
}
